import {
    Directive,
    effect,
    ElementRef,
    forwardRef,
    HostListener,
    input,
    Renderer2,
} from '@angular/core';
import {
    AbstractControl,
    AsyncValidator,
    AsyncValidatorFn,
    ControlValueAccessor,
    NG_ASYNC_VALIDATORS,
    NG_VALUE_ACCESSOR,
    UntypedFormControl,
    ValidationErrors,
} from '@angular/forms';

import { Observable, of } from 'rxjs';

export interface ICustomFile extends File {
    errors?: {
        fileSize?: boolean;
        fileType?: boolean;
        fileExt?: boolean;
    };
}

@Directive({
    selector: 
    // eslint-disable-next-line @angular-eslint/directive-selector
    'input[type=file][accept],input[type=file][formControlName],input[type=file][ngModel]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FileInputAccessor),
            multi: true,
        },
        {
            provide: NG_ASYNC_VALIDATORS,
            useExisting: forwardRef(() => FileInputAccessor),
            multi: true,
        },
    ],
})
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class FileInputAccessor implements ControlValueAccessor, AsyncValidator {
    allowedTypes = input<RegExp | string | string[]>();

    size = input<number>();

    withMeta = input<boolean>();

    maxHeight = input<number>();

    maxWidth = input<number>();

    minHeight = input<number>();

    minWidth = input<number>();

    allowedExt = input<RegExp | string | string[]>();

    validator: AsyncValidatorFn;

    private _allowedExt: RegExp | string | string[];

    @HostListener('change', ['$event.target.files']) onChange = (_: any) => {};
    @HostListener('blur') onTouched = () => {};

    constructor(
        private _renderer: Renderer2,
        private _elementRef: ElementRef
    ) {
        this.validator = this.generateAsyncValidator();
        effect(() => {
            let value = this.allowedExt();
            if (typeof value === 'string') {
                value = value + '$';
            }
            if (value instanceof Array) {
                value = value.join('|') + '$';
            }
            this._allowedExt = value as RegExp | string | string[];
        });
    }

    writeValue(value: any) {
        this._renderer.setProperty(
            this._elementRef.nativeElement,
            'value',
            null
        );
    }

    registerOnChange(fn: (_: any) => {}): void {
        this.onChange = this.onChangeGenerator(fn);
    }

    registerOnTouched(fn: () => {}): void {}

    setDisabledState(isDisabled: boolean): void {
        this._renderer.setProperty(
            this._elementRef.nativeElement,
            'disabled',
            isDisabled
        );
    }

    validate(
        c: AbstractControl
    ): Observable<ValidationErrors | null> | Promise<ValidationErrors | null> {
        return this.validator(c);
    }

    private generateAsyncValidator(): any {
        return (c: UntypedFormControl): Observable<ValidationErrors> => {
            if (!c.value || !c.value.length || c.disabled) return of({});

            const errors: ValidationErrors = {};

            for (const f of c.value) {
                const size = this.size();
                if (size && size < f.size) {
                    f.errors.fileSize = true;
                    errors.fileSize = true;
                }

                if (!this._allowedExt && !this.allowedTypes()) continue;

                const extP = this.generateRegExp(this._allowedExt);
                const typeP = this.generateRegExp(this.allowedTypes());

                if (extP && !extP.test(f.name)) {
                    f.errors.fileExt = true;
                    errors.fileExt = true;
                }

                if (typeP && !typeP.test(f.type)) {
                    f.errors.fileType = true;
                    errors.fileType = true;
                }
            }
            return of(errors);
        };
    }

    private onChangeGenerator(fn: (_: any) => {}): (_: ICustomFile[]) => void {
        return (files: ICustomFile[]) => {
            const fileArr: File[] = [];

            for (const f of files) {
                f.errors = {};
                fileArr.push(f);
            }
            fn(fileArr);
        };
    }

    private generateRegExp(
        pattern: RegExp | string | string[] | undefined
    ): RegExp | null {
        if (!pattern) return null;

        if (pattern instanceof RegExp) {
            return new RegExp(pattern);
        } else if (typeof pattern === 'string') {
            return new RegExp(pattern, 'ig');
        } else if (pattern instanceof Array) {
            return new RegExp(`(${pattern.join('|')})`, 'ig');
        }
        return null;
    }
}
